/*
  Shared FAQ accordion for collection-page question/answer lists
  (.tt-faq, wrapped in [data-faq-accordion]).

  Replaces the old approach of embedding a copy of this same click-handler
  logic as an inline <script> inside each collection template's custom-HTML
  block. Those per-page scripts located their container either via a
  hardcoded Shopify section id (broke whenever Shopify's internal id drifted
  from what was hand-typed into the template) or via document.currentScript
  (unreliable across at least two of those pages for reasons that don't
  matter now that this doesn't depend on it at all). One shared, delegated
  listener sidesteps both failure modes entirely.

  - Event delegation on the container: works even though there's no
    per-button listener to lose track of, and needs no re-binding after a
    Shopify theme-editor section reload swaps the container's innerHTML.
  - data-faq-initialised guards against double-initialising the same
    container (e.g. if this script were ever included twice).
  - Only one answer open at a time per container.
  - aria-expanded / hidden are the actual open-state source of truth; the
    is-open class exists only for styling.
*/
(function () {
  "use strict";

  var QUESTION_SELECTOR = ".tt-faq__question";
  var ITEM_SELECTOR = ".tt-faq__item";
  var ANSWER_SELECTOR = ".tt-faq__answer";

  // Real height animation, not an opacity fade - driven entirely from here
  // so the answer markup (shared across every collection template's FAQ
  // data blob) never has to change. DURATION_MS must stay in sync with
  // wherever this is referenced below; there's no separate CSS transition
  // rule for it because the "transition" style is only ever applied for
  // the length of one open/close, then removed - leaving it as a permanent
  // CSS rule would also animate unrelated height changes (e.g. a window
  // resize reflowing the text while the answer is open).
  var DURATION_MS = 250;
  var reduceMotionQuery =
    window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)");

  function prefersReducedMotion() {
    return !!(reduceMotionQuery && reduceMotionQuery.matches);
  }

  // A fast second click must always animate from wherever the answer
  // actually is right now, not from whatever the previous click assumed -
  // so any in-flight animation's cleanup timer is cancelled first.
  function stopAnimating(answer) {
    if (answer._faqTimer) {
      window.clearTimeout(answer._faqTimer);
      answer._faqTimer = null;
    }
    if (answer._faqRaf) {
      window.cancelAnimationFrame(answer._faqRaf);
      answer._faqRaf = null;
    }
  }

  // getBoundingClientRect (not scrollHeight) so this reads the element's
  // true current height even mid-transition.
  function currentHeight(answer) {
    return answer.getBoundingClientRect().height;
  }

  function closeItem(button) {
    var item = button.closest(ITEM_SELECTOR);
    var answer = item && item.querySelector(ANSWER_SELECTOR);
    button.setAttribute("aria-expanded", "false");
    if (item) item.classList.remove("is-open");
    if (!answer || answer.hidden) return;

    if (prefersReducedMotion()) {
      answer.hidden = true;
      return;
    }

    stopAnimating(answer);
    var startHeight = currentHeight(answer);
    answer.style.overflow = "hidden";
    answer.style.height = startHeight + "px";

    // Wait a frame instead of forcing a synchronous reflow (the old
    // getBoundingClientRect()-as-commit trick) so the browser can batch and
    // schedule this normally - forcing layout synchronously right in the
    // click handler is what caused the visible stutter.
    answer._faqRaf = window.requestAnimationFrame(function () {
      answer.style.transition = "height " + DURATION_MS + "ms ease";
      answer.style.height = "0px";
    });

    answer._faqTimer = window.setTimeout(function () {
      answer.hidden = true;
      answer.style.height = "";
      answer.style.overflow = "";
      answer.style.transition = "";
      answer._faqTimer = null;
    }, DURATION_MS);
  }

  function openItem(button) {
    var item = button.closest(ITEM_SELECTOR);
    var answer = item && item.querySelector(ANSWER_SELECTOR);
    button.setAttribute("aria-expanded", "true");
    if (item) item.classList.add("is-open");
    if (!answer) return;

    if (prefersReducedMotion()) {
      answer.hidden = false;
      return;
    }

    var startHeight = answer.hidden ? 0 : currentHeight(answer);
    stopAnimating(answer);
    answer.hidden = false;
    answer.style.overflow = "hidden";
    answer.style.height = "auto";
    // scrollHeight instead of getBoundingClientRect(): both force the same
    // one unavoidable layout read (we need the natural height to animate
    // to), but this is the only forced read left in this function now.
    var targetHeight = answer.scrollHeight;
    answer.style.height = startHeight + "px";

    // Wait a frame instead of forcing a second synchronous reflow (the old
    // getBoundingClientRect()-as-commit trick) so the browser can batch and
    // schedule this normally - forcing layout synchronously right in the
    // click handler is what caused the visible stutter.
    answer._faqRaf = window.requestAnimationFrame(function () {
      answer.style.transition = "height " + DURATION_MS + "ms ease";
      answer.style.height = targetHeight + "px";
    });

    answer._faqTimer = window.setTimeout(function () {
      answer.style.height = "";
      answer.style.overflow = "";
      answer.style.transition = "";
      answer._faqTimer = null;
    }, DURATION_MS);
  }

  function initialiseFaq(container) {
    if (container.dataset.faqInitialised === "true") return;
    container.dataset.faqInitialised = "true";

    container.addEventListener("click", function (event) {
      var button = event.target.closest(QUESTION_SELECTOR);
      if (!button || !container.contains(button)) return;

      // href="#" isn't used by the new button markup, but guard anyway in
      // case this ever wraps a legacy <a>-based question.
      event.preventDefault();

      var item = button.closest(ITEM_SELECTOR);
      var answer = item && item.querySelector(ANSWER_SELECTOR);
      if (!item || !answer) return;

      var isOpen = button.getAttribute("aria-expanded") === "true";

      container
        .querySelectorAll(QUESTION_SELECTOR + '[aria-expanded="true"]')
        .forEach(function (openButton) {
          if (openButton !== button) closeItem(openButton);
        });

      if (isOpen) {
        closeItem(button);
      } else {
        openItem(button);
      }
    });
  }

  function initialiseAllFaqs(root) {
    (root || document).querySelectorAll("[data-faq-accordion]").forEach(initialiseFaq);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", function () {
      initialiseAllFaqs(document);
    });
  } else {
    initialiseAllFaqs(document);
  }

  // Re-run (safely - the guard above skips anything already initialised,
  // and a swapped-in section is a fresh element with no guard set yet) when
  // the theme editor reloads a section.
  document.addEventListener("shopify:section:load", function (event) {
    initialiseAllFaqs(event.target);
  });
})();
